/**
 * GraphQL mutation definitions matching backend schema.
 */

import { gql } from "@apollo/client";


export const CREATE_TASK = gql`
  mutation CreateTask($input: TaskCreateInput!) {
    createTask(input: $input) {
      success
      message
      task {
        id
        title
        description
        status
        taskType
        projectId
        sectionId
        procurementPackageId
        plotId
        locationId
        dueDate
        responsibleUserId
        createdByUserId
        responsibleUser {
          id
          firstName
          lastName
        }
        version
        createdAt
        updatedAt
      }
    }
  }
`;

export const DELETE_TASK = gql`
  mutation DeleteTask($id: String!) {
    deleteTask(id: $id) {
      success
      message
    }
  }
`;

export const CREATE_RECURRING_TASK_SERIES = gql`
  mutation CreateRecurringTaskSeries($input: RecurringTaskSeriesCreateInput!) {
    createRecurringTaskSeries(input: $input) {
      success
      message
      createdCount
      series {
        id
        frequency
        intervalCount
        weekday
        weekOfMonth
        startDate
        endDate
      }
      firstTask {
        id
        title
        description
        status
        taskType
        projectId
        plotId
        procurementPackageId
        dueDate
        occurrenceDate
        isRecurring
        responsibleUserId
        createdByUserId
        responsibleUser {
          id
          firstName
          lastName
        }
        version
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_TASK_SERIES = gql`
  mutation UpdateTaskSeries($input: TaskSeriesUpdateInput!) {
    updateTaskSeries(input: $input) {
      success
      message
      affectedCount
      task {
        id
        title
        description
        status
        dueDate
        responsibleUserId
        responsibleUser {
          id
          firstName
          lastName
        }
        version
        updatedAt
      }
    }
  }
`;

export const UPDATE_TASK_SERIES_RULE = gql`
  mutation UpdateTaskSeriesRule($input: TaskSeriesRuleUpdateInput!) {
    updateTaskSeriesRule(input: $input) {
      success
      message
      createdCount
      preservedCount
      removedCount
      series {
        id
        frequency
        intervalCount
        weekday
        weekOfMonth
        startDate
        endDate
        rootSeriesId
      }
    }
  }
`;

export const DELETE_TASK_SERIES = gql`
  mutation DeleteTaskSeries($id: String!) {
    deleteTaskSeries(id: $id) {
      success
      message
    }
  }
`;

export const ADD_SUBTASK_TO_TASK_SERIES = gql`
  mutation AddSubtaskToTaskSeries($input: SeriesSubtaskAddInput!) {
    addSubtaskToTaskSeries(input: $input) {
      success
      message
      affectedCount
    }
  }
`;

export const UPDATE_SUBTASK_IN_TASK_SERIES = gql`
  mutation UpdateSubtaskInTaskSeries($input: SeriesSubtaskUpdateInput!) {
    updateSubtaskInTaskSeries(input: $input) {
      success
      message
      affectedCount
    }
  }
`;

export const DELETE_SUBTASK_FROM_TASK_SERIES = gql`
  mutation DeleteSubtaskFromTaskSeries($id: String!) {
    deleteSubtaskFromTaskSeries(id: $id) {
      success
      message
      affectedCount
    }
  }
`;

export const UPDATE_TASK = gql`
  mutation UpdateTask($input: TaskUpdateInput!) {
    updateTask(input: $input) {
      success
      message
      task {
        id
        title
        description
        status
        taskType
        source
        projectId
        sectionId
        locationId
        dueDate
        responsibleUserId
        responsibleUser {
          id
          firstName
          lastName
        }
        version
        createdAt
        updatedAt
      }
    }
  }
`;

// ============================================
// COMMENT MUTATIONS
// ============================================

export const CREATE_TASK_COMMENT = gql`
  mutation CreateTaskComment($input: TaskCommentCreateInput!) {
    createTaskComment(input: $input) {
      success
      message
      comment {
        id
        content
        taskId
        writerId
        version
        createdAt
        updatedAt
        writer {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const GET_TASK_COMMENTS = gql`
  query GetTaskComments($taskId: String!) {
    taskComments(taskId: $taskId) {
      items {
        id
        content
        taskId
        writerId
        version
        createdAt
        updatedAt
        writer {
          id
          firstName
          lastName
        }
      }
      totalCount
    }
  }
`;

// ============================================
// RESPONSIBLE-USER MUTATIONS (project-structure editor)
// ============================================

export const UPDATE_PROCUREMENT_PACKAGE_RESPONSIBLE_USER = gql`
  mutation UpdateProcurementPackageResponsibleUser(
    $input: ProcurementPackageUpdateInput!
  ) {
    updateProcurementPackage(input: $input) {
      success
      message
      procurementPackage {
        id
        responsibleUserId
        version
        responsibleUser {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export const UPDATE_SECTION_RESPONSIBLE_USER = gql`
  mutation UpdateSectionResponsibleUser(
    $input: SectionResponsibleUserUpdateInput!
  ) {
    updateSectionResponsibleUser(input: $input) {
      success
      message
      section {
        id
        responsibleUserId
        version
        responsibleUser {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export const SET_SECTION_LOCATION_RESPONSIBLE = gql`
  mutation SetSectionLocationResponsible(
    $input: SectionLocationResponsibleUpsertInput!
  ) {
    setSectionLocationResponsible(input: $input) {
      success
      message
      sectionLocation {
        id
        sectionId
        locationId
        responsibleUserId
        version
      }
    }
  }
`;

export const UPDATE_TASK_RESPONSIBLE_USER = gql`
  mutation UpdateTaskResponsibleUser($input: TaskUpdateInput!) {
    updateTask(input: $input) {
      success
      message
      task {
        id
        responsibleUserId
        version
        responsibleUser {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

// ============================================
// PROJECT MUTATIONS
// ============================================

export const DELETE_PROJECT = gql`
  mutation DeleteProject($id: String!) {
    deleteProject(id: $id) {
      success
      message
    }
  }
`;

// ============================================
// PROJECT ROLE MUTATIONS
// ============================================

export const CREATE_PROJECT_ROLE = gql`
  mutation CreateProjectRole($input: ProjectRoleCreateInput!) {
    createProjectRole(input: $input) {
      success
      message
      projectRole {
        id
        userId
        projectId
        roleType
        version
        createdAt
      }
    }
  }
`;

export const UPDATE_PROJECT_ROLE = gql`
  mutation UpdateProjectRole($input: ProjectRoleUpdateInput!) {
    updateProjectRole(input: $input) {
      success
      message
      projectRole {
        id
        userId
        projectId
        roleType
        version
        createdAt
      }
    }
  }
`;

export const DELETE_PROJECT_ROLE = gql`
  mutation DeleteProjectRole($id: String!) {
    deleteProjectRole(id: $id) {
      success
      message
    }
  }
`;

// ============================================
// SCAFFOLD PROJECT MUTATION
// ============================================

export const SCAFFOLD_PROJECT = gql`
  mutation ScaffoldProject($input: ScaffoldProjectInput!) {
    scaffoldProject(input: $input) {
      success
      message
      project {
        id
        name
        description
        status
        projectType
        startDate
        objectiveEndDate
        version
        createdAt
        updatedAt
      }
      sectionsCreated
      locationsCreated
      tasksCreated
    }
  }
`;

// ============================================
// USER MUTATIONS
// ============================================

export const SYNC_CURRENT_USER = gql`
  mutation SyncCurrentUser($input: SyncCurrentUserInput!) {
    syncCurrentUser(input: $input) {
      success
      message
      user {
        id
        email
        firstName
        lastName
        isActive
        createdAt
        lastLoginAt
      }
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($input: UserUpdateInput!) {
    updateUser(input: $input) {
      success
      message
      user {
        id
        email
        firstName
        lastName
        accessLevel
        updatedAt
      }
    }
  }
`;

export const SET_LOCATION_SECTION_RESPONSIBILITIES = gql`
  mutation SetLocationSectionResponsibilities($input: SetLocationSectionResponsibilitiesInput!) {
    setLocationSectionResponsibilities(input: $input) {
      success
      message
    }
  }
`;

const MEMO_TASK_FIELDS = `
  id
  ownerUserId
  title
  description
  status
  dueDate
  startedAt
  completedAt
  version
  createdByUserId
  createdByName
  viewerCanEdit
`;

export const CREATE_MEMO_TASK = gql`
  mutation CreateMemoTask($input: CreateMemoTaskInput!) {
    createMemoTask(input: $input) {
      success
      message
      memoTask {
        ${MEMO_TASK_FIELDS}
      }
    }
  }
`;

export const UPDATE_MEMO_TASK = gql`
  mutation UpdateMemoTask($input: UpdateMemoTaskInput!) {
    updateMemoTask(input: $input) {
      success
      message
      memoTask {
        ${MEMO_TASK_FIELDS}
      }
    }
  }
`;

export const DELETE_MEMO_TASK = gql`
  mutation DeleteMemoTask($id: String!, $version: Int!) {
    deleteMemoTask(id: $id, version: $version) {
      success
      message
    }
  }
`;
